import { useState, useEffect } from 'react'
import {
    Box,
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Grid,
    TextField,
    Card,
    CardContent,
    IconButton,
    Chip
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, deleteDoc, doc } from 'firebase/firestore'
import { db } from '../../../config/firebase'
import { Partner } from '@starwave/shared/src/types'
import toast from 'react-hot-toast'

export default function PartnersPage() {
    const [partners, setPartners] = useState<Partner[]>([])
    const [loading, setLoading] = useState(true)

    // Form State
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [share, setShare] = useState('')

    useEffect(() => {
        const q = query(collection(db, 'partners'), orderBy('name', 'asc'))
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Partner[]
            setPartners(data)
            setLoading(false)
        })

        return () => unsubscribe()
    }, [])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!name || !share) return

        try {
            await addDoc(collection(db, 'partners'), {
                name,
                email,
                phone,
                profitSharePercentage: Number(share),
                status: 'active',
                joinedAt: serverTimestamp()
            })
            toast.success('Partner added')
            setName('')
            setEmail('')
            setPhone('')
            setShare('')
        } catch (error) {
            toast.error('Failed to add partner')
        }
    }

    const handleDelete = async (id: string) => {
        if (confirm('Are you sure you want to delete this partner?')) {
            await deleteDoc(doc(db, 'partners', id))
            toast.success('Partner removed')
        }
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                Partner Management
            </Typography>

            <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Add Partner</Typography>
                            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="Full Name"
                                    fullWidth
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                />
                                <TextField
                                    label="Profit Share (%)"
                                    type="number"
                                    fullWidth
                                    value={share}
                                    onChange={(e) => setShare(e.target.value)}
                                    required
                                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                                />
                                <TextField
                                    label="Email"
                                    type="email"
                                    fullWidth
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                                <TextField
                                    label="Phone"
                                    fullWidth
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                />
                                <Button type="submit" variant="contained" startIcon={<AddIcon />}>
                                    Add Partner
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Paper>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Contact</TableCell>
                                        <TableCell align="right">Share (%)</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {partners.map((partner) => (
                                        <TableRow key={partner.id}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>{partner.name}</TableCell>
                                            <TableCell>
                                                <Typography variant="caption" display="block">{partner.email}</Typography>
                                                <Typography variant="caption" display="block">{partner.phone}</Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Chip label={`${partner.profitSharePercentage}%`} color="primary" variant="outlined" />
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={partner.status} color="success" size="small" />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" color="error" onClick={() => handleDelete(partner.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {partners.length === 0 && !loading && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">No partners registered</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    )
}
